/**
 * useChartMind - Hook for ChartMind differential diagnosis generation
 *
 * Calls the AI service workflow endpoint to generate differential diagnoses
 * from a patient encounter transcript. Prompt is managed on the backend.
 */

import { useState, useCallback, useRef } from "react";
import { invokeLLMPrompt } from "services/llmService";
import { retrieveClinicalReferences } from "services/clinicalReferencesService";
import { logChartmindDDXDiagnostic } from "services/chartmindDDXDiagnosticLog";
import { patientContextPayloadKey } from "utils/patientContext";
import { useAuth } from "hooks/useAuth";

// Workflow name for DDX generation (matches Firestore doc ID)
const DDX_WORKFLOW_NAME = "chartmind-diagnosis";

/**
 * Parse LLM response to extract JSON
 */
function parseResponse(content) {
  if (!content) {
    return null;
  }

  const trimmed = content.trim();

  // Check for non-JSON responses
  if (
    trimmed.startsWith("I'm sorry") ||
    trimmed.startsWith("I cannot") ||
    trimmed.startsWith("I don't") ||
    !trimmed.includes("{")
  ) {
    console.log("[useChartMind] LLM returned non-JSON response");
    return {
      primary_diagnoses: [],
      alternative_diagnoses: [],
      clarifications: ["Please provide more information about the symptoms"],
      red_flags: [],
      next_steps: [],
    };
  }

  // Extract JSON from markdown code blocks if present
  let jsonStr = content;
  const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (jsonMatch) {
    jsonStr = jsonMatch[1];
  }

  try {
    return JSON.parse(jsonStr.trim());
  } catch (error) {
    console.error("[useChartMind] Failed to parse JSON:", error);
    console.error("[useChartMind] Content:", jsonStr.substring(0, 500));
    return null;
  }
}

/**
 * useChartMindDDX Hook
 *
 * @param {Object} options - Hook options
 * @param {Function} options.onDataChanged - Callback fired after successful AI response (for auto-save)
 * @param {string} options.promptId - Prompt to generate with. Defaults to ChartMind's own;
 *   ClinicalHUD2 and inpatient encounters pass their own so they can be tuned separately.
 *   Clinical references are still retrieved against ChartMind's prompt, which is where they
 *   are tagged.
 * @param {string} [options.fallbackPromptId] - Used if `promptId` is not set up on this
 *   environment (inpatient falls back to ChartMind's own).
 * @param {string} [options.focus] - Specialty and setting focus, appended to the request
 * @returns {Object} DDX state and actions
 */
function useChartMind({ onDataChanged, promptId = DDX_WORKFLOW_NAME, fallbackPromptId, focus } = {}) {
  const { organizationId } = useAuth();
  const [ddxData, setDdxData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clinicalReferences, setClinicalReferences] = useState([]);
  const [embeddedDocuments, setEmbeddedDocuments] = useState([]);

  // Track last analyzed transcript to prevent duplicate calls
  const lastTranscriptRef = useRef("");
  const isAnalyzingRef = useRef(false);

  /**
   * Analyze transcript and generate differential diagnosis
   */
  /**
   * @param {string} transcript
   * @param {Object|null} patientContext
   * @param {Object} [options]
   * @param {string} [options.workingDiagnosis] - The clinician's own diagnosis, sent ahead of the
   *   transcript so the model keeps it first. ChartMind does not pass it (its gate asks first
   *   and shows the clinician's answer beside the list instead); ClinicalHUD2 does.
   */
  const analyzeDDX = useCallback(
    async (transcript, patientContext = null, { workingDiagnosis = "" } = {}) => {
      // Require minimum transcript length
      if (!transcript || transcript.trim().length < 50) {
        console.log(
          "[useChartMind] Transcript too short:",
          transcript?.length || 0
        );
        return;
      }

      // Prevent duplicate/concurrent calls
      if (isAnalyzingRef.current) {
        console.log("[useChartMind] Already analyzing, skipping");
        return;
      }

      // Skip if neither transcript nor shared-record context has changed.
      const working = String(workingDiagnosis || "").trim();
      // The prompt is part of the key only when it is not ChartMind's own, so ChartMind's key is
      // unchanged and ChartMind V2 can re-run the same transcript under the other prompt.
      const promptPart = promptId === DDX_WORKFLOW_NAME ? "" : `::${promptId}`;
      const dedupKey = `${transcript.trim()}::${patientContextPayloadKey(patientContext)}${working ? `::${working}` : ""}${promptPart}`;
      if (dedupKey === lastTranscriptRef.current) {
        console.log("[useChartMind] Transcript + context unchanged, skipping");
        return;
      }

      isAnalyzingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        console.log("[useChartMind] Calling workflow...", {
          workflowName: promptId,
          organizationId: organizationId || null,
          transcriptLength: transcript.length,
        });

        const [result, refs] = await Promise.all([
          invokeLLMPrompt({
            promptId,
            fallbackPromptId,
            focus,
            userMessage: `${
              working
                ? `WORKING DIAGNOSIS (clinician; keep as rank 1 and add competing diagnoses):\n${working}\n\n`
                : ""
            }PATIENT ENCOUNTER TRANSCRIPT:\n\n${transcript}`,
            organizationId,
            patientContext,
            includeEmbeddedDocuments: true,
            embeddedDocumentsOptions: { chunkLimit: 8 },
          }),
          retrieveClinicalReferences(transcript, {
            promptId: DDX_WORKFLOW_NAME,
          }).catch((err) => {
            console.warn("[useChartMind] Clinical references retrieval failed:", err);
            return [];
          }),
        ]);

        setClinicalReferences(refs);
        setEmbeddedDocuments(result.embeddedDocuments?.documents ?? []);

        console.log("[useChartMind] Workflow response received!", {
          hasResult: !!result,
          hasData: !!result?.data,
          success: result?.data?.success,
          responseLength: result?.output?.length || 0,
          hasMetadata: !!result?.meta,
        });

        const responseText = result.output;
        console.log(
          "[useChartMind] Response text:",
          responseText?.substring(0, 200)
        );

        // Parse response
        const parsed = parseResponse(responseText);
        console.log("[useChartMind] Parsed result:", !!parsed);

        if (parsed) {
          setDdxData(parsed);
          lastTranscriptRef.current = dedupKey;
          console.log("[useChartMind] DDX generated:", {
            primaryCount: parsed.primary_diagnoses?.length || 0,
            alternativeCount: parsed.alternative_diagnoses?.length || 0,
            clarificationsCount: parsed.clarifications?.length || 0,
          });

          const hasAnyDx =
            (parsed.primary_diagnoses?.length || 0) +
              (parsed.alternative_diagnoses?.length || 0) >
            0;
          if (!hasAnyDx) {
            logChartmindDDXDiagnostic({
              event: "chartmind_ddx_empty",
              responseText,
              organizationId,
              transcriptLength: transcript.length,
            });
          }

          // Trigger auto-save after successful AI response
          if (onDataChanged) {
            console.log("[useChartMind] Triggering auto-save callback");
            onDataChanged();
          }
        } else {
          setError("Failed to parse diagnosis response. Please try again.");
          logChartmindDDXDiagnostic({
            event: "chartmind_ddx_parse_failed",
            responseText,
            organizationId,
            transcriptLength: transcript.length,
          });
        }
      } catch (err) {
        console.error("[useChartMind] Error:", err);
        setError(err.message || "Failed to generate diagnosis");
      } finally {
        isAnalyzingRef.current = false;
        setLoading(false);
      }
    },
    [onDataChanged, organizationId, promptId, fallbackPromptId, focus]
  );

  /**
   * Clear all DDX data
   */
  const clearDDX = useCallback(() => {
    setDdxData(null);
    setError(null);
    setClinicalReferences([]);
    setEmbeddedDocuments([]);
    lastTranscriptRef.current = "";
  }, []);

  /**
   * Retry analysis with the same or new transcript.
   *
   * patientContext is threaded through deliberately: DiagnosisStep wires its retry button
   * straight to this with only a transcript, and the old signature dropped the imported
   * record on the floor, so a single retry silently un-did the record import.
   * useChartMindSession wraps this to supply the current context.
   */
  const retry = useCallback(
    (transcript, patientContext = null, options = undefined) => {
      lastTranscriptRef.current = ""; // Force re-analysis
      setError(null);
      if (transcript) {
        analyzeDDX(transcript, patientContext, options);
      }
    },
    [analyzeDDX]
  );

  /**
   * Directly set ddxData (for hydrating from saved session)
   */
  const setDdxDataDirectly = useCallback((data) => {
    console.log('[useChartMind] setDdxDataDirectly called with:', data);
    setDdxData(data);
  }, []);

  /**
   * Restore source documents (for hydrating from saved session).
   * Resets to empty when the saved session has none.
   */
  const hydrateSourceDocuments = useCallback((data) => {
    setClinicalReferences(data?.clinicalReferences || []);
    setEmbeddedDocuments(data?.embeddedDocuments || []);
  }, []);

  return {
    // State
    ddxData,
    loading,
    error,
    clinicalReferences,
    embeddedDocuments,

    // Computed
    hasDiagnoses:
      (ddxData?.primary_diagnoses?.length || 0) +
        (ddxData?.alternative_diagnoses?.length || 0) >
      0,

    // Actions
    analyzeDDX,
    clearDDX,
    retry,
    setDdxData: setDdxDataDirectly, // For session hydration
    hydrateSourceDocuments, // For session hydration
  };
}

export default useChartMind;
